import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ParsedError } from '@app/shared/models/parsed-error';
import { getTranslationKey } from '@app/translate/models/translatable';
import { isObject, parseMaybeJson } from '@app/utils/common';
import { ErrorDescription } from '@app/utils/error/error-description';
import { ErrorDetails } from '@app/utils/error/error-details';
import { isPortalError, PortalError } from '@app/utils/error/portal-error';
import { RawErrorDetails } from '@app/utils/error/raw-error-details';
import { loggerFor } from '@app/utils/logger';
import { isNotBlank } from '@app/utils/string';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ErrorTransformerService {
  private logger = loggerFor('ErrorTransformer');

  constructor(private translateService: TranslateService) {}

  /**
   * Get an error description that can be used to display information as to why the error occurred.
   * @param cause Cause of the error being described.
   * @param statusTitleMapping
   */
  describeError(
    cause: HttpErrorResponse | ParsedError | Error | PortalError,
    statusTitleMapping?: Record<number, string>
  ): ErrorDescription | null {
    let description: ErrorDescription | null = null;

    if (cause instanceof HttpErrorResponse) {
      const details = this.resolveErrorDetails(cause.error ?? cause);

      let message: string;
      let title: string;

      if (statusTitleMapping != null) {
        title = statusTitleMapping[cause.status] ?? this.getHttpResponseTitle(cause);
      } else {
        title = this.getHttpResponseTitle(cause);
      }

      if (details?.resultCode != null) {
        message = details.resultCode;
      } else if (details?.message != null) {
        message = details.message;
      } else if (details?.exception != null) {
        message = details.exception;
      }

      description = {
        details,
        message,
        title,
      };
    } else if (cause instanceof ParsedError) {
      const details = this.resolveErrorDetails(cause.response);

      let message: string | undefined = details?.resultCode ?? details?.exception;

      if (message != null) {
        const translation = this.translateService.instant(message);

        if (translation === cause.header) {
          message = undefined;
        }
      }

      description = {
        details,
        message,
        title: cause.header,
      };
    } else if (cause instanceof Error) {
      const details = this.resolveErrorDetails({
        message: cause.message,
      });

      description = {
        details,
        title: details?.resultCode ?? details?.exception,
      };
    } else if (isPortalError(cause)) {
      description = this.describeError(cause.cause, statusTitleMapping);
      if (cause.i18n != null) {
        description = {
          ...description,
          title: getTranslationKey(cause.i18n),
        };
      }

      if (cause.description != null) {
        description.details = {
          ...(description.details ?? {}),
          message: cause.description,
          messageIsHtml: cause.descriptionIsHtml ?? false,
        };
      }
    }

    this.logKnownDevelopmentIssues(cause);

    return description;
  }

  flattenErrors(errors: PortalError[], id?: string): PortalError[] {
    const flat = [];

    for (const error of errors) {
      if (error.cause != null && 'errors' in error.cause) {
        flat.push(...this.flattenErrors(error.cause.errors, [id, error.id].filter(isNotBlank).join('_')));
      } else if (isPortalError(error.cause)) {
        flat.push(...this.flattenErrors([error.cause], [id, error.id].filter(isNotBlank).join('_')));
      } else {
        if (id != null) {
          flat.push({
            ...error,
            id: `${id}_${error.id}`,
          });
        } else {
          flat.push(error);
        }
      }
    }

    return flat;
  }

  private getHttpResponseTitle(cause: HttpErrorResponse): `http:${number}` | `${number} ${string}` {
    return this.translationExists(`http:${cause.status}`)
      ? `http:${cause.status}`
      : `${cause.status} ${cause.statusText}`;
  }

  private resolveErrorDetails(details: RawErrorDetails | null): ErrorDetails | null {
    let enhancedDetails: ErrorDetails = {};

    if (details != null) {
      if (details.exception != null) {
        enhancedDetails.exception = this.resolve(details.exception, 'exception', details.method);
      }

      if (details.resultCode != null) {
        enhancedDetails.resultCode = this.resolve(details.resultCode, 'resultCode', details.method);
      }

      if (details.violatingFields != null) {
        enhancedDetails.violatingFields = details.violatingFields;
      }

      if (details.hint != null) {
        enhancedDetails.hint = this.resolve(details.hint, 'hint', details.method);
      }

      if (details.message != null) {
        enhancedDetails.message = this.resolve(details.message, 'message', details.method);
      }

      if (details.data != null) {
        const maybeDetails = parseMaybeJson(details.data);

        if (typeof maybeDetails !== 'string') {
          enhancedDetails = this.resolveErrorDetails(maybeDetails);
        } else {
          enhancedDetails.raw = maybeDetails;
        }
      }

      if (details.details != null) {
        enhancedDetails.details = this.resolve(details.details, 'details', details.method);
      }
    }

    return Object.keys(enhancedDetails).length > 0 ? enhancedDetails : null;
  }

  private resolve(message: string, prefix: string, method?: string): string {
    let key: string = message;

    if (
      isNotBlank(prefix) &&
      isNotBlank(method) &&
      this.translationExists(`${prefix}:${method}:${message}`)
    ) {
      key = `${prefix}:${method}:${message}`;
    } else if (isNotBlank(prefix) && this.translationExists(`${prefix}:${message}`)) {
      key = `${prefix}:${message}`;
    }

    return key;
  }

  private translationExists(i18nKey: string): boolean {
    return this.translateService.instant(i18nKey) !== i18nKey;
  }

  /**
   * Transform a given error response into a representation that can be used to display information as to why the error occurred.
   * @param response
   */
  public responseToMsg(response: any): ParsedError {
    const translateWithPrefix = (prefix, msgKey) => {
      let fullMsgKey = `${prefix}:${msgKey}`;
      let translation = this.translateService.instant(fullMsgKey, response.data);

      this.logger.debug(`Attempting translation for ${fullMsgKey}`);

      if (translation === fullMsgKey) {
        return msgKey;
      } else {
        return translation;
      }
    };

    const translateWithPrefixAndMethod = (prefix, msgKey, method) => {
      let fullMsgKey = `${prefix}:${method}:${msgKey}`;
      let translation = this.translateService.instant(fullMsgKey);

      this.logger.debug(`Attempting translation for ${fullMsgKey}`);

      if (translation === fullMsgKey) {
        return translateWithPrefix(prefix, msgKey);
      } else {
        return translation;
      }
    };

    const responseBodyToMsg: (data) => ParsedError = (data) => {
      let translatedMessage;
      let translatedResultCode;

      this.logger.debug('ErrorMsgService converting response body to error message: ', data);

      let translatedException = _toMsg(
        translateWithPrefixAndMethod('exception', data['exception'], data['method'])
      );
      //  data['exception']
      //   ? _toMsg(translateWithPrefixAndMethod('exception', data['exception'], data['method']))
      //   : null

      if (data['message']) {
        translatedMessage = translateWithPrefixAndMethod('message', data['message'], data['method']);
        this.logger.debug("ErrorMsgService translated 'message' to: ", translatedMessage);
        if (translatedException != null) {
          this.logger.debug('ErrorMsgService: adding message to exception: ', translatedMessage);
          translatedException = _toMsg(translatedException.header, translatedMessage);
        } else {
          translatedException = _toMsg(translatedMessage);
        }
      }

      if (data['resultCode']) {
        translatedResultCode = _toMsg(
          translateWithPrefixAndMethod('resultCode', data['resultCode'], data['method'])
        );
        if (translatedException != null) {
          translatedException = _toMsg(translatedException.header, translatedResultCode.header);
        } else {
          translatedException = _toMsg(translatedResultCode.header);
        }
      }

      this.logger.debug('Returning exception translation:', translatedException);

      return new ParsedError({
        ...translatedException,
        response,
      });
    };

    const responseHttpErrorToMsg: (resp) => ParsedError = (resp) => {
      this.logger.debug('ErrorMsgService converting HTTP error response to error message: ', resp);
      let translation = _toMsg(translateWithPrefix('message', resp['data'].substr(0, 300)));

      if (resp['statusText']) {
        // Fallback to translate the status message provided (with HTTP error code)
        translation = _toMsg(
          translateWithPrefixAndMethod('http', resp['statusText'], resp['status']),
          translation
        );
      } else if (resp['status']) {
        // Fallback to translate the numeric HTTP error code
        translation = _toMsg(translateWithPrefix('http', resp['status']), translation);
      }

      return new ParsedError({
        ...translation,
        response,
      });
    };

    const _toMsg = (header, text = void 0) => {
      return {
        header: header,
        response,
        text: text,
      };
    };

    const translateHttpError = (response: HttpErrorResponse) => {
      const translatedError = responseBodyToMsg(response.error);

      return new ParsedError({
        header: translatedError.header,
        response: translatedError.response,
        text: translatedError.text,
      });
    };

    this.logger.debug('ErrorMsgService trying to translate response', response);

    if (response != null) {
      // In case the http response is of type 'text' for some responses but 'application/json' for others
      // This happens e.g. when initiating the reset password flow
      if (typeof response === 'string') {
        response = parseMaybeJson(response);
      }

      if (isPortalError(response)) {
        return this.handlePortalError(response);
      }

      if (response.exception !== undefined) {
        return responseBodyToMsg(response);
      } else if (isObject(response.data)) {
        const config = response.config;

        if (config !== undefined && config.method !== undefined) {
          response.data.method = response.config.method;
        }

        return responseBodyToMsg(response.data);
      } else if (isObject(response) && response.data != null) {
        return responseHttpErrorToMsg(response);
      } else if (response instanceof HttpErrorResponse) {
        return translateHttpError(response);
      } else {
        return new ParsedError({
          header: response['code'] ? response['code'] : this.translateService.instant('generic.error'),
          text: response['message'] ? response['message'] : '',
          response: response,
        });
      }
    } else {
      return new ParsedError({
        header: this.translateService.instant('generic.error'),
        response,
      });
    }
  }

  /**
   * Only here for backwards compatibility with the current ParsedError and error-alert component.
   * @TODO Remove after the portal has been refactored to use the new PortalError.
   */
  private handlePortalError(response: any) {
    let unpackedError: PortalError = response;

    while (isPortalError(unpackedError.cause)) {
      unpackedError = unpackedError.cause;
    }

    let parsedError: ParsedError;

    if (unpackedError.cause instanceof HttpErrorResponse) {
      parsedError = this.responseToMsg(unpackedError.cause.error);
    } else {
      parsedError = this.responseToMsg(unpackedError.cause);
    }

    parsedError.response.id = unpackedError.id;

    return parsedError;
  }

  private logKnownDevelopmentIssues(error: HttpErrorResponse | ParsedError | Error | PortalError): void {
    if (environment.production === false) {
      if (error != null && 'stack' in error && error.stack.includes('_fnCalculateColumnWidths')) {
        console.warn('Check if the datatable on this page has the correct amount of columns');
      }
    }
  }
}
